import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  PackageCheck,
  Radio,
  Rocket,
} from 'lucide-react';
import React from 'react';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  formatArtifactSummary,
  formatRelativeDate,
} from '../utils/formatters';
import { assessProjectReadiness } from '../utils/project-readiness';

function OverviewPage() {
  const {
    workspace,
    sortedPromotions,
    artifactById,
    channels,
    isEnvironmentVisible,
    selectNav,
  } = useWorkspace();

  const {
    releaseApps,
    releaseAppVersions,
    runtimeConfigs,
    artifacts,
    promotions,
  } = workspace;

  const visibleRuntimeRules = runtimeConfigs.filter((config) =>
    isEnvironmentVisible(config.environment)
  );
  const visiblePromotions = sortedPromotions.filter((promotion) =>
    isEnvironmentVisible(promotion.environment)
  );
  const latestDeployment = visiblePromotions[0];
  const readiness = assessProjectReadiness({
    releaseApps,
    releaseAppVersions,
    artifacts,
    channels,
    promotions,
    runtimeConfigs: visibleRuntimeRules,
  });
  const attentionChecks = readiness.checks
    .filter((check) => check.status !== 'healthy')
    .sort((a, b) => Number(b.status === 'blocked') - Number(a.status === 'blocked'));

  return (
    <>
      <section className="projectStatusStrip" aria-label="Project status">
        <div>
          <span>Attention</span>
          <strong>{readiness.blocked} blocking</strong>
          <small>{readiness.warnings} more to review</small>
        </div>
        <div>
          <span>Serving targets</span>
          <strong>{readiness.servingTargets} of {readiness.totalTargets}</strong>
          <small>{readiness.totalTargets - readiness.servingTargets} not serving a bundle</small>
        </div>
        <div>
          <span>Restore points</span>
          <strong>{readiness.recoverableTargets} of {readiness.servingTargets}</strong>
          <small>serving targets recoverable</small>
        </div>
        <div>
          <span>Last deployment</span>
          <strong>{latestDeployment ? formatRelativeDate(latestDeployment.created) : 'None yet'}</strong>
          <small>{latestDeployment ? 'most recent target change' : 'No release history'}</small>
        </div>
      </section>

      <SectionCard
        className="operationalReadiness"
        label="Operational readiness"
        title={attentionChecks.length > 0 ? 'What needs attention' : 'Ready to operate'}
        description={attentionChecks.length > 0
          ? 'These gaps can stop a deployment or leave it without a restore point.'
          : 'Every configured target has a runtime rule, a bundle and a restore point.'}
        action={
          <button type="button" className="panelControl" onClick={() => selectNav('diagnostics')}>
            Full health report
          </button>
        }
      >
        {attentionChecks.length > 0 ? (
          <div className="readinessList">
            {attentionChecks.map((item) => (
              <button
                key={item.id}
                type="button"
                className="readinessRow"
                onClick={() => selectNav(item.page)}
              >
                <span className={item.status === 'blocked' ? 'readinessMissing' : 'readinessWarning'}>
                  <AlertTriangle size={18} />
                </span>
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </span>
                <StatusChip tone={item.status === 'blocked' ? 'danger' : 'warn'}>
                  {item.status === 'blocked' ? 'Blocking' : 'Review'}
                </StatusChip>
                <span className="readinessAction">{item.action} <ArrowRight size={13} /></span>
              </button>
            ))}
          </div>
        ) : (
          <div className="readinessComplete">
            <CheckCircle2 size={18} />
            <span><strong>Release setup is complete</strong><small>Open Health checks to test the live API connection.</small></span>
          </div>
        )}
        <div className="projectQuickActions" aria-label="Common project actions">
          <button type="button" onClick={() => selectNav('promotions')}><Rocket size={14} /> Deploy update</button>
          <button type="button" onClick={() => selectNav('channels')}><Radio size={14} /> View live state</button>
          <button type="button" onClick={() => selectNav('releases')}><PackageCheck size={14} /> Browse bundles</button>
        </div>
      </SectionCard>

      <div className="overviewDetailGrid overviewDetailGrid--single">
        <SectionCard
          label="Recent changes"
          title="Latest deployments"
          description="The last five times a bundle changed on a release target."
          action={
            <button type="button" className="panelControl" onClick={() => selectNav('promotions')}>
              View history
            </button>
          }
        >
          {visiblePromotions.length > 0 ? (
            <div className="recentDeploymentList">
              {visiblePromotions.slice(0, 5).map((promotion) => {
                const artifact = artifactById.get(promotion.releaseArtifactId);
                const target = channels.find(
                  (channel) => channel.releaseChannelId === promotion.releaseChannelId
                );
                return (
                  <div key={promotion.releasePromotionId} className="recentDeploymentRow">
                    <span className="recentDeploymentIcon"><PackageCheck size={16} /></span>
                    <span>
                      <strong>{formatArtifactSummary(artifact)}</strong>
                      <small>
                        {target?.name || `${promotion.environment}/${promotion.channel}`}
                      </small>
                    </span>
                    <span className="recentDeploymentTime">
                      <Clock3 size={13} />
                      {formatRelativeDate(promotion.created)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              compact
              icon={<Rocket size={18} />}
              title="No deployments yet"
              description="Deploy a bundle and its history will appear here."
              action={
                <button type="button" className="secondaryButton" onClick={() => selectNav('promotions')}>
                  Start deployment
                </button>
              }
            />
          )}
        </SectionCard>
      </div>
    </>
  );
}

export { OverviewPage };
